// @ts-check
import Chart from 'chart.js/auto';

// Shared styling for all chart axes and gridlines. scaleStyle lowkey AI generated.
const scaleStyle = {
  ticks: {
    color: '#e8eadf'                   // Clean off-white text matching site theme
  },
  grid: {
    color: 'rgba(255, 255, 255, 0.1)'  // Subtle translucent gridlines that don't compete with data lines
  },
  border: {
    color: 'rgba(255, 255, 255, 0.2)'  // Soft axis border line
  }
};

const aPC = document.getElementById('apparentPowerChart');
const rPC = document.getElementById('realPowerChart');
const vC = document.getElementById('voltageChart');
const iC = document.getElementById('currentChart');

// Creating the charts to display. Major Apparent Power,
// with minor Real Power, Voltage and Current charts.

const initTime = new Date().toLocaleTimeString();

const aPCChart = new Chart(aPC, {
  type: 'line',
  data: {
    labels: [initTime],
    datasets: [{
      label: 'Apparent Power [VA]',
      data: [],
      borderColor: '#32a846',

      tension: 0,
      fill: false
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Turns off the legend
      }
    },
    scales: {
      x: scaleStyle,
      y: {
        ...scaleStyle,
        beginAtZero: true
      }
    }
  }
})

const rPCChart = new Chart(rPC, {
  type: 'line',
  data: {
    labels: [initTime],
    datasets: [{
      label: 'Real Power [W]',
      data: [],
      borderColor: '#304fff',

      tension: 0,
      fill: false
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Turns off the legend
      }
    },
    scales: {
      x: scaleStyle,
      y: {
        ...scaleStyle,
        beginAtZero: true
      }
    }
  }
})

const vCChart = new Chart(vC, {
  type: 'line',
  data: {
    labels: [initTime],
    datasets: [{
      label: 'Voltage [V]',
      data: [],
      borderColor: '#F2415C',

      tension: 0,
      fill: false
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Turns off the legend
      }
    },
    scales: {
      x: scaleStyle,
      y: {
        ...scaleStyle,
        beginAtZero: true
      }
    }
  }
})

const iCChart = new Chart(iC, {
  type: 'line',
  data: {
    labels: [initTime],
    datasets: [{
      label: 'Current [A]',
      data: [],
      borderColor: '#ffd930',

      tension: 0,
      fill: false
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // Turns off the legend
      }
    },
    scales: {
      x: scaleStyle,
      y: {
        ...scaleStyle,
        beginAtZero: true
      }
    }
  }
})

// Funciton for adding a point
// Dynamically handles x-axis time scaling and shifting
// Recieves data from python server script and presents on browser

function addPointMajor(chart, value) {
  const timestamp = new Date().toLocaleTimeString();

  chart.data.labels.push(timestamp);
  chart.data.datasets[0].data.push(value);

  // Keep last 20 on chart
  if (chart.data.labels.length > 30) {
    chart.data.labels.shift();
    chart.data.datasets[0].data.shift();
  }

  chart.update('none');
}

function addPointMinor(chart, value) {
  const timestamp = new Date().toLocaleTimeString();

  chart.data.labels.push(timestamp);
  chart.data.datasets[0].data.push(value);

  // Keep last 20 on chart
  if (chart.data.labels.length > 10) {
    chart.data.labels.shift();
    chart.data.datasets[0].data.shift();
  }

  chart.update('none');
}

function updateStatDisplay(id, value, decimals = 1) {
  const el = document.getElementById(id);
  if (el && typeof value === 'number') {
    el.textContent = value.toFixed(decimals);
  }
}

function handleEsp32Readings(data) {
  // 1. Synchronized Batch Update: updates all 4 graphs simultaneously!
  if (data.type === 'batch') {
    addPointMinor(vCChart, data.voltage);
    addPointMinor(iCChart, data.current);
    addPointMinor(rPCChart, data.power);
    addPointMajor(aPCChart, data.power_s);

    updateStatDisplay('voltageValue', data.voltage, 1);
    updateStatDisplay('currentValue', data.current, 2);
    updateStatDisplay('realPowerValue', data.power, 1);
    updateStatDisplay('apparentPowerValue', data.power_s, 1);
    return;
  }

  // 2. Legacy single-reading fallback
  if (data.unit === 'VA') {
    addPointMajor(aPCChart, data.value);
    updateStatDisplay('apparentPowerValue', data.value, 1);
  } else if (data.unit === 'W') {
    addPointMinor(rPCChart, data.value);
    updateStatDisplay('realPowerValue', data.value, 1);
  } else if (data.unit === 'V') {
    addPointMinor(vCChart, data.value);
    updateStatDisplay('voltageValue', data.value, 1);
  } else if (data.unit === 'A') {
    addPointMinor(iCChart, data.value);
    updateStatDisplay('currentValue', data.value, 2);
  }
}

let lastTimestamp = null;

function processReading(reading) {
  if (reading && reading.timestamp && reading.timestamp !== lastTimestamp) {
    lastTimestamp = reading.timestamp;
    handleEsp32Readings(reading);
  }
}

// 1. Try Server-Sent Events stream (for local server.py / dev)
try {
  const sseUrl = window.location.port === '1234'
    ? `http://${window.location.hostname}:8080/events`
    : '/events';
  const eventSource = new EventSource(sseUrl);

  eventSource.onmessage = (event) => {
    try {
      const reading = JSON.parse(event.data);
      processReading(reading);
    } catch (_) {}
  };

  eventSource.onerror = () => {
    // Gracefully close if SSE endpoint is unavailable (e.g. serverless Cloudflare)
    eventSource.close();
  };
} catch (_) {}

// 2. Fast Polling fallback (for Cloudflare Pages Functions + D1)
async function pollTelemetry() {
  if (document.hidden) return;
  try {
    const res = await fetch('/api/readings', { cache: 'no-store' });
    if (res.ok) {
      const reading = await res.json();
      processReading(reading);
    }
  } catch (_) {}
}

setInterval(pollTelemetry, 1000);
pollTelemetry();

// Keyboard navigation: press Left or Right arrow keys to rotate major chart
window.addEventListener('keydown', (e) => {
  if (e.key === 'ArrowLeft') {
    const prev = document.querySelector('.prev-arrow');
    if (prev instanceof HTMLAnchorElement) prev.click();
  } else if (e.key === 'ArrowRight') {
    const next = document.querySelector('.next-arrow');
    if (next instanceof HTMLAnchorElement) next.click();
  }
});
